// src/components/Seller/SellerProfile.js
import React, { useEffect, useState } from "react";
import "../../styles/SellerProfile.css";
import config from "../../config";

export default function SellerProfile() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    name: "",
    contactName: "",
    phoneNumber: "",
    addressLine: "",
    city: "",
    state: "",
    pincode: "",
    gstNumber: "",
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const token = localStorage.getItem("seller_id_token");

  // --------- LOAD PROFILE ----------
  const loadProfile = async () => {
    if (!token) {
      setError("Missing seller token. Please logout and login again.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${config.baseUrl}/api/seller/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load profile");
      }

      const data = await res.json();
      setProfile(data);

      setForm({
        name: data.name || "",
        contactName: data.contactName || "",
        phoneNumber: data.phoneNumber || "",
        addressLine: data.addressLine || "",
        city: data.city || "",
        state: data.state || "",
        pincode: data.pincode || "",
        gstNumber: data.gstNumber || "",
      });
    } catch (err) {
      console.error("Profile load error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------- HANDLERS ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccess("");
    setError("");
  };

  const handleCancel = () => {
    // Reset form to last saved profile
    if (profile) {
      setForm({
        name: profile.name || "",
        contactName: profile.contactName || "",
        phoneNumber: profile.phoneNumber || "",
        addressLine: profile.addressLine || "",
        city: profile.city || "",
        state: profile.state || "",
        pincode: profile.pincode || "",
        gstNumber: profile.gstNumber || "",
      });
    }
    setIsEditing(false);
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    if (!token) {
      setError("Missing seller token. Please logout and login again.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${config.baseUrl}/api/seller/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          contactName: form.contactName,
          phoneNumber: form.phoneNumber,
          addressLine: form.addressLine,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
          gstNumber: form.gstNumber,
        }),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(body.message || "Failed to update profile");
      }

      setProfile(body);
      setIsEditing(false);
      setSuccess("Profile updated successfully.");
    } catch (err) {
      console.error("Profile save error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // --------- RENDER ----------
  if (loading && !profile) {
    return (
      <div className="seller-profile-container">
        <div className="seller-profile-card">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile && error) {
    return (
      <div className="seller-profile-container">
        <div className="seller-profile-card">
          <p className="seller-profile-error">{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="seller-profile-container">
      {/* HEADER */}
      <div className="seller-profile-header">
        <div>
          <h2>Seller Profile</h2>
          <p className="seller-profile-subtitle">
            Manage your shop details and contact information.
          </p>
        </div>
        <div className="seller-profile-header-right">
          <div className="seller-profile-meta">
            <span>
              <strong>Seller ID:</strong> {profile.id}
            </span>
            <span>
              <strong>Email:</strong> {profile.email}
            </span>
          </div>
          <span className={`seller-status-badge status-${(profile.status || "ACTIVE").toLowerCase()}`}>
            {profile.status || "ACTIVE"}
          </span>
        </div>
      </div>

      {/* ALERTS */}
      {error && (
        <div className="seller-profile-alert error">
          {error}
        </div>
      )}
      {success && (
        <div className="seller-profile-alert success">
          {success}
        </div>
      )}

      {/* ACTION BAR */}
      <div className="seller-profile-toolbar">
        {!isEditing && (
          <button className="seller-btn-primary" onClick={handleEdit}>
            ✏️ Edit Profile
          </button>
        )}
        {isEditing && (
          <>
            <button
              className="seller-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Changes"}
            </button>
            <button
              className="seller-btn-secondary"
              type="button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </>
        )}
      </div>

      {/* CONTENT GRID */}
      <div className="seller-profile-grid">
        {/* SHOP DETAILS */}
        <section className="seller-card-section">
          <h3>Shop Details</h3>
          <div className="seller-form-row">
            <div className="seller-form-group full-width">
              <label>Shop / Brand Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Your shop / brand name"
              />
            </div>
          </div>

          <div className="seller-form-row">
            <div className="seller-form-group">
              <label>Contact Person</label>
              <input
                type="text"
                name="contactName"
                value={form.contactName}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Contact person name"
              />
            </div>
            <div className="seller-form-group">
              <label>Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Mobile number"
              />
            </div>
          </div>
        </section>

        {/* ADDRESS + GST */}
        <section className="seller-card-section">
          <h3>Shop Address</h3>
          <div className="seller-form-row">
            <div className="seller-form-group full-width">
              <label>Address Line</label>
              <input
                type="text"
                name="addressLine"
                value={form.addressLine}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Street / Area"
              />
            </div>
          </div>

          <div className="seller-form-row">
            <div className="seller-form-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="City"
              />
            </div>
            <div className="seller-form-group">
              <label>State</label>
              <input
                type="text"
                name="state"
                value={form.state}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="State"
              />
            </div>
            <div className="seller-form-group">
              <label>Pincode</label>
              <input
                type="text"
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Pincode"
              />
            </div>
          </div>

          <div className="seller-form-row">
            <div className="seller-form-group">
              <label>GST Number</label>
              <input
                type="text"
                name="gstNumber"
                value={form.gstNumber}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="GST Number"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
